import { useAuthStore } from "@/store/authStore.js";
import { invalidateShioriRequests } from "@/store/shiori-store.js";
import { invalidateShioriMemberRequests } from "@/store/shiori-member-store.js";
import { invalidateShioriDestinationImageWorkflows } from "@/utils/ensure-shiori-destination-image.js";
import {
  getSessionSnapshot,
  isCurrentSessionSnapshot,
  BROADCAST_SENDER_ID,
} from "@/api/session-context.js";

let removeActiveListeners = null;
const listenerKey = Symbol.for("rabiitrip.auth-storage-sync.cleanup");
const AUTH_SYNC_CHANNEL = "rabiitrip_auth_sync";

const normalizeToken = (value) =>
  typeof value === "string" && value ? value : null;

export function resetPrivateStores(pinia) {
  invalidateShioriDestinationImageWorkflows();
  invalidateShioriRequests();
  invalidateShioriMemberRequests();
  for (const [id, store] of pinia._s) {
    if (id === "auth") continue;
    try {
      if (typeof store.$reset === "function") {
        store.$reset();
      }
    } catch (_error) {
      // Setup-syntax stores do not implement $reset() by default
    }
  }
}

export function installAuthStorageSync({ pinia, router }) {
  removeActiveListeners?.();
  window[listenerKey]?.();
  let transitionId = 0;

  const appElement = () => document.querySelector("#app");
  const setTransitioning = (active) => {
    appElement()?.classList.toggle("session-boundary-active", active);
  };
  const safeReplace = async (path) => {
    try {
      if (router.currentRoute.value.path !== path) await router.replace(path);
    } catch (_error) {
      // A newer session transition may supersede this navigation.
    }
  };

  const handleTokenChange = (nextToken, previousToken) => {
    if (previousToken === nextToken) return;

    transitionId += 1;
    const activeTransition = transitionId;
    const finishTransition = () => {
      if (activeTransition === transitionId) setTransitioning(false);
    };
    setTransitioning(true);
    const session = getSessionSnapshot();
    resetPrivateStores(pinia);

    const authStore = useAuthStore(pinia);
    authStore.$patch({ token: nextToken, user: null, error: null });

    if (!nextToken) {
      void safeReplace("/login").finally(finishTransition);
      return;
    }

    void safeReplace("/mypage");
    void authStore.fetchUser().then(async (user) => {
      if (!isCurrentSessionSnapshot(session)) {
        finishTransition();
        return;
      }
      if (!user) {
        authStore.logout(session);
        await safeReplace("/login");
      } else {
        await safeReplace("/mypage");
      }
      finishTransition();
    });
  };

  const handleStorage = (event) => {
    if (event.key !== "token" || event.storageArea !== window.localStorage) {
      return;
    }
    const previousToken = normalizeToken(event.oldValue);
    const nextToken = normalizeToken(event.newValue);
    handleTokenChange(nextToken, previousToken);
  };

  let channel = null;
  const handleMessage = (event) => {
    if (
      event?.data?.type === "session_change" &&
      event.data.sender !== BROADCAST_SENDER_ID
    ) {
      const nextToken = normalizeToken(event.data.nextToken);
      const previousToken = normalizeToken(event.data.previousToken);
      handleTokenChange(nextToken, previousToken);
    }
  };

  try {
    if (typeof BroadcastChannel !== "undefined") {
      channel = new BroadcastChannel(AUTH_SYNC_CHANNEL);
      channel.addEventListener("message", handleMessage);
    }
  } catch (_error) {
    channel = null;
  }

  window.addEventListener("storage", handleStorage);
  const cleanup = () => {
    window.removeEventListener("storage", handleStorage);
    if (channel) {
      channel.removeEventListener("message", handleMessage);
      try {
        channel.close();
      } catch (_error) {
        // Ignore channel close errors
      }
      channel = null;
    }
    setTransitioning(false);
    if (window[listenerKey] === cleanup) delete window[listenerKey];
    removeActiveListeners = null;
  };
  removeActiveListeners = cleanup;
  window[listenerKey] = cleanup;
  return cleanup;
}
